// Constants for RTSP URL validation
export const RTSP_VALIDATION_CONSTANTS = {
  // Regex patterns
  RTSP_PATTERN: /^rtsp:\/\/([^:]+):(.+)@([^:\/]+):(\d+)(\/.*)?$/,
  // No port — rtsp://username:password@host/path or rtsp://username:password@host/path?query
  // Accepts both IP addresses and hostnames
  // Supports optional query strings
  RTSP_PATTERN_WITHOUTPORT: /^rtsp:\/\/([^:@]+):([^@]+)@([^:\/\s]+)(\/[^?#]*)?(\?[^#]*)?$/,
  // Full URL with port and optional query string
  // Matches: rtsp://username:password@host:port/path?key=value
  // Accepts both IP addresses and hostnames
  RTSP_PATTERN_WITH_QUERY: /^rtsp:\/\/([^:@]+):([^@]+)@([^:\/\s]+):(\d+)(\/[^?#]*)?(\?[^#]*)?$/,
  IP_PATTERN: (() => {
  const O = "(?:25[0-5]|2[0-4]\\d|[01]?\\d\\d?)";
  return new RegExp(`^(?:${O}\\.){3}${O}$`);
  })(),
  DOMAIN_PATTERN: /^([a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/,

  // Error messages
  ERRORS: {
    URL_REQUIRED: "RTSP URL is required",
    INVALID_FORMAT: "Invalid RTSP URL format. Expected: rtsp://username:password@host:port/path",
    INVALID_URL: "Please fix all RTSP URL validation errors before submitting",
    URL_NOT_FOUND: "No RTSP URL found",
    INVALID_FORMAT_GENERAL: "Invalid RTSP URL format",
    ERROR_PROCESSING: "Error processing RTSP URL",
    USERNAME_REQUIRED: "Username is required in RTSP URL",
    PASSWORD_REQUIRED: "Password is required in RTSP URL",
    HOST_REQUIRED: "Host is required in RTSP URL",
    INVALID_HOST: "Invalid host format. Must be a valid IP address or hostname",
    INVALID_PORT: "Port must be a number between 1 and 65535",
    INVALID_PATH: "Path must start with '/' if provided",
    INVALID_PATH_ENCODING: "Path must not contain '%'",
    MISSING_HOST_SEPARATOR: "Invalid RTSP URL format - missing host separator",
    CREDENTIALS_MISMATCH: "User credentials do not match with RTSP URL, please update the Username and Password in the credentials section above."
  },
  
  // Port validation
  PORT_RANGE: {
    MIN: 1,
    MAX: 65535
  },
  
  // Helper text
  HELPER_TEXT: "Example: rtsp://<username>:<password>@192.168.2.233:8554/b1b9aebc-eb9b-488b-b65f-446655442014/PPE/FHD/stream",
  HELPER_TEXT_PATH: "Complete the RTSP url by providing the stream path. Ex: Streaming/Channels/101",

};
// RTSP URL validation function
  export const validateRtspUrl = (url) => {
    if (!url || typeof url !== 'string') {
      return RTSP_VALIDATION_CONSTANTS.ERRORS.URL_REQUIRED;
    }

    // Try with port first, then without
    let matches = url.match(RTSP_VALIDATION_CONSTANTS.RTSP_PATTERN);

    if (!matches) {
      // Try pattern with query string support
      matches = url.match(RTSP_VALIDATION_CONSTANTS.RTSP_PATTERN_WITH_QUERY);
    }

    if (!matches) {
      // Try pattern without port
      matches = url.match(RTSP_VALIDATION_CONSTANTS.RTSP_PATTERN_WITHOUTPORT);
    }

    if (!matches) {
      return RTSP_VALIDATION_CONSTANTS.ERRORS.INVALID_FORMAT;
    }

    const username = matches[1];
    const password = matches[2];
    const host = matches[3];

    // Validate username
    if (!username || username.trim() === '') {
      return RTSP_VALIDATION_CONSTANTS.ERRORS.USERNAME_REQUIRED;
    }

    // Validate password
    if (!password || password.trim() === '') {
      return RTSP_VALIDATION_CONSTANTS.ERRORS.PASSWORD_REQUIRED;
    }

    // Validate host
    if (!host || host.trim() === '') {
      return RTSP_VALIDATION_CONSTANTS.ERRORS.HOST_REQUIRED;
    }

    return null; // No errors
  };

  // Check if credentials match between form and RTSP URL
  export const validateCredentialsMatch = (url, formUsername, formPassword) => {
    if (!url) return null;

    const rtspPattern = RTSP_VALIDATION_CONSTANTS.RTSP_PATTERN;
    const matches = url.match(rtspPattern);
    
    if (!matches) return null;

    const [, urlUsername, urlPassword] = matches;

    if (formUsername && formPassword) {
      if (formUsername !== urlUsername || formPassword !== urlPassword) {
        return RTSP_VALIDATION_CONSTANTS.ERRORS.CREDENTIALS_MISMATCH;
      }
    }

    return null;
  };

// Utility function to extract RTSP URL components
// Returns decoded values (actual username/password, not URL-encoded)
export const extractRtspUrlComponents = (url) => {
  if (!url) return null;

  let matches;
  let patternType;

  // Try with port first - RTSP_PATTERN has 5 capture groups
  matches = url.match(RTSP_VALIDATION_CONSTANTS.RTSP_PATTERN);
  if (matches) {
    patternType = 'standard';
  } else {
    // Try with query string - RTSP_PATTERN_WITH_QUERY has 6 capture groups
    matches = url.match(RTSP_VALIDATION_CONSTANTS.RTSP_PATTERN_WITH_QUERY);
    if (matches) {
      patternType = 'withQuery';
    } else {
      // Try without port - RTSP_PATTERN_WITHOUTPORT has 5 capture groups
      matches = url.match(RTSP_VALIDATION_CONSTANTS.RTSP_PATTERN_WITHOUTPORT);
      if (matches) {
        patternType = 'noPort';
      }
    }
  }

  if (!matches) return null;

  // Extract based on pattern type to handle different capture group positions
  let username, password, host, port, path;

  if (patternType === 'noPort') {
    // RTSP_PATTERN_WITHOUTPORT: [_, username, password, host, path, query]
    [, username, password, host, path] = matches;
    port = null;
  } else if (patternType === 'withQuery') {
    // RTSP_PATTERN_WITH_QUERY: [_, username, password, host, port, path, query]
    [, username, password, host, port, path] = matches;
  } else {
    // RTSP_PATTERN: [_, username, password, host, port, path]
    [, username, password, host, port, path] = matches;
  }

  // Decode username and password to get actual values
  const decodedUsername = safeDecodeURIComponent(username);
  const decodedPassword = safeDecodeURIComponent(password);

  return {
    username: decodedUsername,
    password: decodedPassword,
    host,
    port: port ? parseInt(port, 10) : null,
    path: path || '/'
  };
};

// Helper function to safely decode URI components
function safeDecodeURIComponent(str) {
  try {
    return decodeURIComponent(str);
  } catch (e) {
    // If decoding fails, return as-is
    return str;
  }
}